import { autoUpdate, flip, FloatingPortal, offset, shift, useFloating } from '@floating-ui/react';
import { TFunction } from 'i18next';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { DefaultTheme, useTheme } from 'styled-components';
import { useOnClickOutside } from 'usehooks-ts';

import { GeodataModel } from '../../../../../../typings/api/skymap/rest/v0/.common';
import { localStorageUtils } from '../../../../utils/local-storage-utils';
import { publish, SubscriptionTopic } from '../../../../viewer/messaging/pubsub';
import { errorUtils, translateApiErrors } from '../../../api/error-handling';
import { useDialog } from '../../../hooks/use-dialog';
import { CheckBox } from '../../check-box/check-box';
import { DataTable, DataTableColumns, DataTableSortType } from '../../data-table/data-table';
import { Icon } from '../../icon/icon';
import { IconPanel } from '../../icon-panel/icon-panel';
import { OverlayLoader } from '../../overlay-loader/overlay-loader';
import { Stack } from '../../stack/stack';
import { TextBox } from '../../text-box/text-box';
import { GcpRow } from '../gcp-parser';
import { convertGcpRowsToGcpCollectionModel } from '../geodata-configuration/parse-gcp-rows';
import { useSaveGeodataMutation } from '../geodata-configuration/use-save-geodata-mutation';
import { GeodataContext } from '../geodata-state';
import { DeleteGcpConfirmationDialog } from './delete-gcp-confirmation-dialog';
import { GcpOptimizationStatusIcon } from './gcp-optimization-status-icon';
import { GcpStatusIcon } from './gcp-status-icon';
import { GcpWithErrors, PlaceMarkersContext } from './place-markers-state';
import { UploadGcpDialog } from './upload-gcp-dialog';
import { usePatchGcpMutation } from './use-patch-gcp-mutation';

const gcpColumnOptionKeys = [
  'northing',
  'easting',
  'height',
  'rmseE',
  'rmseN',
  'rmseH',
  'rmseTotal',
  'biasE',
  'biasN',
  'biasH',
  'biasTotal',
  'errorPointcloudH',
] as const;

type GcpColumnOptionKey = (typeof gcpColumnOptionKeys)[number];

/** Default when stored value is missing, empty, or invalid */
const FALLBACK_VISIBLE_COLUMNS: Record<GcpColumnOptionKey, boolean> = {
  northing: false,
  easting: false,
  height: true,
  rmseE: false,
  rmseN: false,
  rmseH: false,
  rmseTotal: true,
  biasE: false,
  biasN: false,
  biasH: false,
  biasTotal: false,
  errorPointcloudH: false,
};

const validColumnKeysSet = new Set<string>(gcpColumnOptionKeys);

function parseStoredColumnVisibility(
  stored: string | null | undefined,
): Record<GcpColumnOptionKey, boolean> | null {
  if (stored == null || stored.trim() === '') {
    return null;
  }
  const keys = stored
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  if (keys.length === 0) {
    return null;
  }
  for (const k of keys) {
    if (!validColumnKeysSet.has(k)) {
      return null;
    }
  }
  const result = {} as Record<GcpColumnOptionKey, boolean>;
  for (const key of gcpColumnOptionKeys) {
    result[key] = keys.includes(key);
  }
  return result;
}

function getInitialColumnVisibility(): Record<GcpColumnOptionKey, boolean> {
  const stored = localStorageUtils.getUserItemOrDefault('alignerGcpListColumns', '');
  const parsed = parseStoredColumnVisibility(stored || null);
  return parsed ?? FALLBACK_VISIBLE_COLUMNS;
}

const getColumnOptionLabels = (t: TFunction): Record<GcpColumnOptionKey, string> => ({
  northing: 'Northing',
  easting: 'Easting',
  height: 'Height',
  rmseE: 'ΔE (RMSE)',
  rmseN: 'ΔN (RMSE)',
  rmseH: 'ΔH (RMSE)',
  rmseTotal: t('gcpList.rmseTotal', { ns: 'cloudProcessing' }),
  biasE: 'ΔE (Bias)',
  biasN: 'ΔN (Bias)',
  biasH: 'ΔH (Bias)',
  biasTotal: t('gcpList.biasTotal', { ns: 'cloudProcessing' }),
  errorPointcloudH: t('gcpList.errorPointcloudH', { ns: 'cloudProcessing' }),
});

const pageSize = 15;

const searchKeys = ['name'] as const;

function filterItems(items: GcpWithErrors[], searchText: string): GcpWithErrors[] {
  const text = searchText.trim().toLowerCase();
  if (text.length === 0) {
    return items;
  }

  return items.filter((item) =>
    searchKeys.some((x) => item[x]?.toLocaleLowerCase().includes(text)),
  );
}

const NoGcpPanel = () => {
  const { t } = useTranslation();

  return (
    <NoGcpPanelStyle>
      <IconPanel
        header={{ icon: { icon: ['fad', 'magnifying-glass'], opacity: '50%', size: '4x' } }}
        responsiveness={{ compressed: false }}
      >
        {{
          title: t('gcpList.noGcp', { ns: 'cloudProcessing' }),
          info: t('gcpList.noGcpInfo', { ns: 'cloudProcessing' }),
        }}
      </IconPanel>
    </NoGcpPanelStyle>
  );
};

const NoGcpPanelStyle = styled.div`
  display: flex;
  padding: 1em;
`;

const OptionsTriggerWrap = styled.div`
  display: inline-flex;
`;

const OptionsPopover = styled.div`
  z-index: 1000;
  background: white;
  border: 1px solid ${(p) => p.theme.color.gray.light};
  border-radius: 4px;
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);
  padding: 0.5em 0;
  min-width: 160px;
`;

const OptionsPopoverTitle = styled.div`
  padding: 0.25em 0.75em 0.5em;
  font-weight: 600;
  font-size: 0.9em;
  color: ${(p) => p.theme.color.gray.dark};
  border-bottom: 1px solid ${(p) => p.theme.color.gray.light};
  margin-bottom: 0.25em;
`;

const OptionRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5em;
  padding: 0.35em 0.75em;
  cursor: pointer;
  font-size: 0.9em;
  &:hover {
    background: ${(p) => p.theme.color.gray.lightest};
  }
  input {
    margin: 0;
    cursor: pointer;
  }
`;

const numberFormatter = (value: number) => value.toFixed(3);

const createColumns = (
  t: TFunction,
  handleToggleStatus: (id: string) => void,
  handleDeleteGcpClick: (id: string) => void,
  geodata: GeodataModel,
  theme: DefaultTheme,
  columnVisibility: Record<GcpColumnOptionKey, boolean>,
  columnOptionLabels: Record<GcpColumnOptionKey, string>,
): DataTableColumns<GcpWithErrors> => {
  const baseColumns: DataTableColumns<GcpWithErrors> = {
    statusIcon: {
      title: t('aligner.table.status', { ns: 'cloudProcessing' }),
      alignment: 'center',
      width: '25px',
      padding: 0,
      sortableBy: { type: DataTableSortType.STRING, columnKey: 'status' },
      unfilterable: true,
      render: (item: GcpWithErrors) => {
        const gcpModel = geodata.gcpCollection?.points.find((gcp) => gcp.id === item.id);
        if (!gcpModel) {
          return null;
        }
        return <GcpStatusIcon model={gcpModel} t={t} onToggleStatus={handleToggleStatus} />;
      },
    },
    optimizationStatus: {
      title: t('aligner.table.optimizationStatus.title', { ns: 'cloudProcessing' }),
      alignment: 'center',
      padding: 0,
      width: '25px',
      sortableBy: { type: DataTableSortType.STRING, columnKey: 'optimizationStatus' },
      unfilterable: true,
      render: (item: GcpWithErrors) => (
        <GcpOptimizationStatusIcon optimizationStatus={item.optimizationStatus} t={t} />
      ),
    },
    name: {
      sortableBy: { type: DataTableSortType.STRING },
      unfilterable: true,
      title: t('name', { ns: 'common' }),
    },
    northing: {
      sortableBy: { type: DataTableSortType.NUMBER },
      unfilterable: true,
      title: columnOptionLabels.northing,
      formatter: numberFormatter,
      hidden: !columnVisibility.northing,
    },
    easting: {
      sortableBy: { type: DataTableSortType.NUMBER },
      unfilterable: true,
      title: columnOptionLabels.easting,
      formatter: numberFormatter,
      hidden: !columnVisibility.easting,
    },
    height: {
      sortableBy: { type: DataTableSortType.NUMBER },
      unfilterable: true,
      title: columnOptionLabels.height,
      formatter: numberFormatter,
      hidden: !columnVisibility.height,
    },
    biasN: {
      sortableBy: { type: DataTableSortType.NUMBER, columnKey: 'errorY' },
      unfilterable: true,
      alignment: 'left',
      title: columnOptionLabels.biasN,
      render: (item) => numberFormatter(item.errorY),
      hidden: !columnVisibility.biasN,
    },
    biasE: {
      sortableBy: { type: DataTableSortType.NUMBER, columnKey: 'errorX' },
      unfilterable: true,
      alignment: 'left',
      title: columnOptionLabels.biasE,
      render: (item) => numberFormatter(item.errorX),
      hidden: !columnVisibility.biasE,
    },
    biasH: {
      sortableBy: { type: DataTableSortType.NUMBER, columnKey: 'errorZ' },
      unfilterable: true,
      alignment: 'left',
      title: columnOptionLabels.biasH,
      render: (item) => numberFormatter(item.errorZ),
      hidden: !columnVisibility.biasH,
    },
    biasTotal: {
      sortableBy: { type: DataTableSortType.NUMBER, columnKey: 'errorTotal' },
      unfilterable: true,
      alignment: 'left',
      title: columnOptionLabels.biasTotal,
      render: (item) => numberFormatter(item.rmseTotal),
      hidden: !columnVisibility.biasTotal,
    },
    rmseN: {
      sortableBy: { type: DataTableSortType.NUMBER, columnKey: 'errorY' },
      unfilterable: true,
      alignment: 'left',
      title: columnOptionLabels.rmseN,
      render: (item) => numberFormatter(item.errorY),
      hidden: !columnVisibility.rmseN,
    },
    rmseE: {
      sortableBy: { type: DataTableSortType.NUMBER, columnKey: 'errorX' },
      unfilterable: true,
      alignment: 'left',
      title: columnOptionLabels.rmseE,
      render: (item) => numberFormatter(item.errorX),
      hidden: !columnVisibility.rmseE,
    },
    rmseH: {
      sortableBy: { type: DataTableSortType.NUMBER, columnKey: 'errorZ' },
      unfilterable: true,
      alignment: 'left',
      title: columnOptionLabels.rmseH,
      render: (item) => numberFormatter(item.errorZ),
      hidden: !columnVisibility.rmseH,
    },
    rmseTotal: {
      sortableBy: { type: DataTableSortType.NUMBER, columnKey: 'rmseTotal' },
      unfilterable: true,
      alignment: 'left',
      title: columnOptionLabels.rmseTotal,
      render: (item) => numberFormatter(item.rmseTotal),
      hidden: !columnVisibility.rmseTotal,
    },
    errorPointcloudH: {
      sortableBy: { type: DataTableSortType.NUMBER },
      unfilterable: true,
      alignment: 'left',
      title: columnOptionLabels.errorPointcloudH,
      render: (item) => (item.errorPointcloudZ ? numberFormatter(item.errorPointcloudZ) : '-'),
      hidden: !columnVisibility.errorPointcloudH,
    },
    actions: {
      alignment: 'right',
      width: '0',
      padding: 0,
      shrink: true,
      unfilterable: true,
      showOnlyOnHover: true,
      render: (item: GcpWithErrors) => (
        <Icon
          color={theme.color.gray.medium}
          icon={['fas', 'trash-alt']}
          style={{ cursor: 'pointer' }}
          title={t('delete', { ns: 'common' })}
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteGcpClick(item.id);
          }}
        />
      ),
    },
  };
  return baseColumns;
};

const getNextStatus = (
  currentStatus: 'ENABLED' | 'DISABLED' | 'CHECKPOINT',
): 'ENABLED' | 'DISABLED' | 'CHECKPOINT' => {
  switch (currentStatus) {
    case 'ENABLED':
      return 'DISABLED';
    case 'DISABLED':
      return 'CHECKPOINT';
    case 'CHECKPOINT':
      return 'ENABLED';
  }
};

export const GcpList = () => {
  const { geodata, refetchGeodata } = useContext(GeodataContext);
  const { gcp, selectGcp, gcpsWithErrors, refetchMarkers } = useContext(PlaceMarkersContext);
  const { t } = useTranslation();
  const theme = useTheme();
  const { patchGcpAsync } = usePatchGcpMutation();

  const { saveQuery } = useSaveGeodataMutation({ geodataId: geodata.id });

  const [searchText, setSearchText] = useState('');
  const [columnVisibility, setColumnVisibility] = useState<Record<GcpColumnOptionKey, boolean>>(
    getInitialColumnVisibility,
  );
  const [optionsOpen, setOptionsOpen] = useState(false);
  const deleteGcpDialog = useDialog();
  const uploadGcpDialog = useDialog();
  const [gcpToDelete, setGcpToDelete] = useState<{ id: string; name: string } | null>(null);

  const handleFileUpload = useCallback(() => {
    uploadGcpDialog.show();
  }, [uploadGcpDialog]);

  const handleImportGcp = useCallback(
    async (data: { filename: string; gcpPoints: GcpRow[] }) => {
      if (!geodata) {
        return;
      }

      try {
        if (data.gcpPoints.length === 0) {
          publish(SubscriptionTopic.ToastrMessage, {
            type: 'error',
            message: t('geodataSetup.uploadGcp.noValidPoints', { ns: 'cloudProcessing' }),
          });
          return;
        }

        // Convert existing GCP points to GcpRow format
        const existingGcpRows: GcpRow[] =
          geodata.gcpCollection?.points.map((x) => ({
            name: { value: x.name, status: 'valid' },
            e: { value: x.enu[0].toString(), status: 'valid_geoid' },
            n: { value: x.enu[1].toString(), status: 'valid_geoid' },
            h: { value: x.enu[2].toString(), status: 'valid_format' },
            included: true,
          })) ?? [];

        // Merge existing points with newly imported points
        // Use a Map to avoid duplicates based on name (case-insensitive)
        const mergedPointsMap = new Map<string, GcpRow>();

        // Add existing points first
        existingGcpRows.forEach((row) => {
          const key = row.name.value.trim().toLowerCase();
          if (key) {
            mergedPointsMap.set(key, row);
          }
        });

        // Add new points, overwriting existing ones with the same name
        data.gcpPoints.forEach((row) => {
          const key = row.name.value.trim().toLowerCase();
          if (key) {
            mergedPointsMap.set(key, row);
          }
        });

        const mergedPoints = Array.from(mergedPointsMap.values());

        // Convert merged points to GcpCollectionModel
        const gcpCollection = convertGcpRowsToGcpCollectionModel(data.filename, mergedPoints);

        if (!gcpCollection) {
          publish(SubscriptionTopic.ToastrMessage, {
            type: 'error',
            message: t('geodataSetup.uploadGcp.noValidPoints', { ns: 'cloudProcessing' }),
          });
          return;
        }

        // Save the merged collection
        await saveQuery.mutateAsync({
          name: geodata.name,
          options: {
            gsd: geodata.gsd,
            objMaxTriangles: geodata.objMaxTriangles,
            rollingShutter: geodata.rollingShutter,
          },
          gcpCollection,
        });

        publish(SubscriptionTopic.ToastrMessage, {
          type: 'success',
          message: t('gcpList.uploadSuccess', { ns: 'cloudProcessing' }),
        });
      } catch (err) {
        console.error('Failed to import GCP file:', err);
        publish(SubscriptionTopic.ToastrMessage, {
          type: 'error',
          message:
            err instanceof Error
              ? err.message
              : t('geodataSetup.uploadGcp.failedToParse', { ns: 'cloudProcessing' }),
        });
        throw err; // Re-throw to let the dialog handle the error
      }
    },
    [geodata, saveQuery, t],
  );

  const handleToggleStatus = useCallback(
    async (id: string) => {
      if (!geodata) {
        console.error('No geodata available');
        return;
      }

      // Find the GCP from geodata instead of items to avoid dependency issues
      const gcpModel = geodata.gcpCollection?.points.find((gcp) => gcp.id === id);
      if (!gcpModel) {
        console.error('GCP not found');
        return;
      }

      const newStatus = getNextStatus(gcpModel.status);

      try {
        await patchGcpAsync({
          geodataId: geodata.id,
          gcpId: id,
          status: newStatus,
        });
      } catch (error) {
        const apiError = errorUtils.toApiErrorResponse(error);
        if (apiError) {
          const translatedErrors = translateApiErrors(apiError);
          for (const translatedError of translatedErrors) {
            publish(SubscriptionTopic.ToastrMessage, {
              type: 'error',
              message: translatedError.errorMessage,
            });
          }
        } else {
          // If error is not an API error, show generic error message
          publish(SubscriptionTopic.ToastrMessage, {
            type: 'error',
            message: t('unexpectedError', { ns: 'common' }),
          });
        }
      }
    },
    [geodata, patchGcpAsync, t],
  );

  const handleDeleteGcpClick = useCallback(
    (id: string) => {
      const gcpModel = geodata.gcpCollection?.points.find((gcp) => gcp.id === id);
      if (gcpModel) {
        setGcpToDelete({ id, name: gcpModel.name });
        deleteGcpDialog.show();
      }
    },
    [geodata, deleteGcpDialog],
  );

  const handleDeleteGcp = useCallback(
    async (id: string) => {
      if (!geodata || !geodata.gcpCollection) {
        return;
      }

      // Filter out the deleted GCP point
      const remainingPoints = geodata.gcpCollection.points.filter((gcp) => gcp.id !== id);

      // Convert remaining points to GcpRow format
      const remainingGcpRows: GcpRow[] = remainingPoints.map((x) => ({
        name: { value: x.name, status: 'valid' },
        e: { value: x.enu[0].toString(), status: 'valid_geoid' },
        n: { value: x.enu[1].toString(), status: 'valid_geoid' },
        h: { value: x.enu[2].toString(), status: 'valid_format' },
        included: true,
      }));

      // Convert to GcpCollectionModel
      // If no points remain, set to null to delete the collection
      const gcpCollection =
        remainingGcpRows.length > 0
          ? convertGcpRowsToGcpCollectionModel(geodata.gcpCollection.name, remainingGcpRows)
          : null;

      // Save the updated collection
      await saveQuery.mutateAsync({
        name: geodata.name,
        options: {
          gsd: geodata.gsd,
          objMaxTriangles: geodata.objMaxTriangles,
          rollingShutter: geodata.rollingShutter,
        },
        gcpCollection,
      });

      void refetchMarkers();
      publish(SubscriptionTopic.ToastrMessage, {
        type: 'success',
        message: t('gcpList.deleteSuccess', { ns: 'cloudProcessing' }),
      });
    },
    [geodata, saveQuery, t, refetchMarkers],
  );

  const filteredItems = useMemo(
    () => filterItems(gcpsWithErrors, searchText),
    [gcpsWithErrors, searchText],
  );

  const renderNoImagesPanel = gcpsWithErrors.length === 0;

  const highlightRow = useCallback((item: GcpWithErrors) => item.id === gcp?.id, [gcp]);

  const columnOptionLabels = useMemo(() => getColumnOptionLabels(t), [t]);
  const columns = useMemo(
    () =>
      createColumns(
        t,
        handleToggleStatus,
        handleDeleteGcpClick,
        geodata,
        theme,
        columnVisibility,
        columnOptionLabels,
      ),
    [
      t,
      handleToggleStatus,
      handleDeleteGcpClick,
      geodata,
      theme,
      columnVisibility,
      columnOptionLabels,
    ],
  );

  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-end',
    middleware: [offset(4), flip(), shift()],
    whileElementsMounted: autoUpdate,
  });

  const isWithinRef = useCallback((e: MouseEvent, ref: HTMLElement | null) => {
    if (!ref || e.clientX == null || e.clientY == null) {
      return false;
    }
    const rect = ref.getBoundingClientRect();
    return (
      e.clientX >= rect.left &&
      e.clientX <= rect.right &&
      e.clientY >= rect.top &&
      e.clientY <= rect.bottom
    );
  }, []);

  useOnClickOutside(refs.floating as React.RefObject<HTMLElement>, (e) => {
    if (
      optionsOpen &&
      !isWithinRef(e as MouseEvent, refs.reference.current as HTMLElement | null)
    ) {
      setOptionsOpen(false);
    }
  });

  const toggleColumn = useCallback((key: GcpColumnOptionKey) => {
    setColumnVisibility((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      const visibleKeys = gcpColumnOptionKeys.filter((k) => next[k]);
      localStorageUtils.setUserItem('alignerGcpListColumns', visibleKeys.join(','));
      return next;
    });
  }, []);

  return (
    <OverlayLoader visible={false}>
      <Stack spacing={1}>
        <Stack alignItems="center" direction="row" spacing={0.5}>
          <TextBox
            placeholder={t('search', { ns: 'common' })}
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
            }}
          />
          <Icon
            color={theme.color.gray.dark}
            fixedWidth={true}
            icon={['fad', 'upload']}
            title={t('gcpList.uploadGcpFile', { ns: 'cloudProcessing' })}
            onClick={handleFileUpload}
            onHoverStyle={{ icon: ['fas', 'upload'] }}
          />
          <OptionsTriggerWrap ref={refs.setReference}>
            <Icon
              color={optionsOpen ? theme.color.green : theme.color.gray.dark}
              fixedWidth={true}
              icon={['fad', 'sliders']}
              title={t('gcpList.columnOptions', { ns: 'cloudProcessing' })}
              onClick={() => setOptionsOpen((o) => !o)}
              onHoverStyle={{ icon: ['fas', 'sliders'] }}
            />
          </OptionsTriggerWrap>
          {optionsOpen && (
            <FloatingPortal>
              <OptionsPopover ref={refs.setFloating} style={floatingStyles}>
                <OptionsPopoverTitle>
                  {t('gcpList.columnOptions', { ns: 'cloudProcessing' })}
                </OptionsPopoverTitle>
                {(Object.keys(columnOptionLabels) as GcpColumnOptionKey[]).map((key) => (
                  <OptionRow key={key}>
                    <CheckBox checked={columnVisibility[key]} onChange={() => toggleColumn(key)}>
                      {columnOptionLabels[key]}
                    </CheckBox>
                  </OptionRow>
                ))}
              </OptionsPopover>
            </FloatingPortal>
          )}
          <Icon
            color={theme.color.gray.dark}
            fixedWidth={true}
            icon={['fad', 'refresh']}
            title={t('refreshList', { ns: 'common' })}
            onClick={() => refetchGeodata()}
            onHoverStyle={{ icon: ['fas', 'refresh'] }}
          />
        </Stack>

        {renderNoImagesPanel ? (
          <NoGcpPanel />
        ) : (
          <DataTable
            columns={columns}
            fixedLayout={true}
            highlightRow={highlightRow}
            items={filteredItems}
            pageSize={pageSize}
            onRowClicked={(item) => {
              const gcpModel = geodata.gcpCollection?.points.find((gcp) => gcp.id === item.id);
              selectGcp(gcpModel);
            }}
          />
        )}
      </Stack>
      {gcpToDelete &&
        deleteGcpDialog.render(
          <DeleteGcpConfirmationDialog
            dialog={deleteGcpDialog}
            gcpName={gcpToDelete.name}
            onConfirm={async () => {
              await handleDeleteGcp(gcpToDelete.id);
              setGcpToDelete(null);
            }}
          />,
        )}
      {uploadGcpDialog.render(
        <UploadGcpDialog dialog={uploadGcpDialog} onImport={handleImportGcp} />,
      )}
    </OverlayLoader>
  );
};
